import { useCallback, useEffect, useRef, useState } from "react";
import { buildEvent, decide } from "../callGate";

export function useEnrichGate(config, stopwords, run) {
  const state = useRef({ previous: "", called: "", baseline: null, times: [], busy: false,
    timer: null, latest: null, revision: 0, editAt: Date.now(), mounted: true });
  const runRef = useRef(run);
  runRef.current = run;
  const [stats, setStats] = useState({ edits: 0, calls: 0, negatives: 0, last: null, outcome: null });
  const pump = useRef(null);
  pump.current = async () => {
    const s = state.current;
    clearTimeout(s.timer);
    if (!s.mounted || s.busy || !s.latest) return;
    const now = Date.now();
    s.times = s.times.filter((t) => now - t < 60000);
    const wait = Math.max(0, (s.times[s.times.length - 1] || 0) + config.min_interval_ms - now,
      s.times.length >= config.max_calls_per_minute ? s.times[0] + 60000 - now : 0);
    if (wait) { s.timer = setTimeout(() => pump.current(), wait); return; }
    const job = s.latest;
    s.latest = null;
    s.busy = true;
    s.times.push(now);
    s.called = job.text;
    s.baseline = job.meta.gate.specificity;
    setStats((v) => ({ ...v, calls: v.calls + 1 }));
    try {
      const response = await runRef.current(job.text, job.meta);
      if (s.mounted && response) setStats((v) => ({ ...v,
        negatives: v.negatives + (response.check?.outcome === "no_match" ? 1 : 0),
        outcome: response.check?.outcome || null }));
    } catch (_) {
      // Failures are not negative matches. New text remains eligible; no retry loop.
      s.called = "";
      s.baseline = null;
    } finally {
      s.busy = false;
      if (s.latest && s.mounted) {
        // Re-evaluate queued text against the just-completed request, not an old baseline.
        const next = s.latest;
        const measurement = buildEvent(next.text, next.text, next.cursor, s.called, config, stopwords, s.baseline);
        const d = decide(config, { ...measurement, insert_chars: next.meta.insert_chars,
          inserted_words: next.meta.gate.inserted_words }, s.times.length > 0);
        if (next.meta.force || d.action !== "skip") s.timer = setTimeout(() => pump.current(), config.debounce_ms);
        else s.latest = null;
      }
    }
  };

  const onEdit = useCallback((text, cursor, reason) => {
    const s = state.current, now = Date.now();
    const ev = buildEvent(s.previous, text, cursor, s.called, config, stopwords, s.baseline);
    const d = reason && text.trim() ? { ...ev, rule_id: reason, action: "call_now" } : decide(config, ev, s.times.length > 0);
    s.revision++;
    const meta = { rev: s.revision, call_rule: d.rule_id, gate: ev, force: Boolean(reason),
      insert_chars: ev.insert_chars, insert_gap_ms: now - s.editAt,
      source: d.rule_id === "large_insert" ? "dictation_or_paste" : "typed" };
    s.previous = text;
    s.editAt = now;
    clearTimeout(s.timer);
    s.latest = d.action === "skip" ? null : { text, cursor, meta };
    setStats((v) => ({ ...v, edits: v.edits + 1, last: d, outcome: null }));
    if (s.latest) s.timer = setTimeout(() => pump.current(), d.action === "call_now" ? 0 : config.debounce_ms);
  }, [config, stopwords]);

  useEffect(() => {
    state.current.mounted = true;
    const s = state.current;
    return () => { s.mounted = false; s.latest = null; clearTimeout(s.timer); };
  }, []);
  return { onEdit, force: (text, reason) => onEdit(text, text.length, reason), stats };
}